import i18n from "i18next";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// Intl puts a no-break space after the currency symbol: a plain one reads (and is found) the same
const plainSpaces = (text: string) => text.replace(/\u00a0|\u202f/g, " ");

/** The editor's language: prices are written as the person reads them (R$ 45,00; $45.00). */
const locale = () => i18n.language || "pt-BR";

/** R$ 45,00 in the account's currency (none: ""). */
export const formatCents = (cents: number | null | undefined, currency = "BRL") =>
  cents === null || cents === undefined
    ? ""
    : plainSpaces(new Intl.NumberFormat(locale(), { style: "currency", currency }).format(cents / 100));

/**
 * The currency's symbol for a price field's prefix, with the space the language puts before the value ("R$ " in
 * Portuguese, "$" in English). Kept as Intl's no-break space: a plain one at the end of the prefix wouldn't show.
 */
export const currencySymbol = (currency: string) => {
  const parts = new Intl.NumberFormat(locale(), { style: "currency", currency }).formatToParts(0);
  const index = parts.findIndex(part => part.type === "currency");
  if (index < 0) return currency;
  const next = parts[index + 1];
  const space = next?.type === "literal" && !next.value.trim() ? "\u00a0" : "";
  return parts[index].value + space;
};

/** 45,00 (45.00 in English) for the price field (none: ""). */
export const centsToField = (cents: number | null | undefined) =>
  cents === null || cents === undefined
    ? ""
    : (cents / 100).toLocaleString(locale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false,
      });

// the digits, then the cents (one or two digits) after a "," or a "."; the digits may have thousands marks of the
// other kind, a group of three each ("1.045,50", "1,045.50", "1.045")
const PRICE = /^(?:(\d+)|(\d{1,3})([.,])(\d{3}(?:\3\d{3})*))(?:([.,])(\d{1,2}))?$/;

/** "45", "45,5", "45.50", "1.045,50", "1,045.50" → cents; "" → null; anything else → NaN. */
export const fieldToCents = (text: string): number | null => {
  const clean = text.trim().replace(/^R\$\s*/, "");
  if (clean === "") return null;
  const match = PRICE.exec(clean);
  // the cents' mark can't also be the thousands' one ("1.045.50")
  if (!match || (match[5] && match[5] === match[3])) return NaN;
  const whole = match[1] ?? `${match[2]}${match[4].replace(/[.,]/g, "")}`;
  return Number(whole) * 100 + Number((match[6] ?? "").padEnd(2, "0"));
};

/** "+5511912345678" → "+55 11 91234 5678", "+12125551234" → "+1 212 555 1234" (as it came, when it isn't one). */
export const formatPhone = (phone: string) => parsePhoneNumberFromString(phone)?.formatInternational() ?? phone;
